import React, { useState, useRef, useEffect } from "react";
import {
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Popper,
  ClickAwayListener,
  InputLabel,
} from "@mui/material";

interface MultiSelectWithSearchFieldProps {
  name: string;
  label?: string;
  selectedValues: string[] | any;
  onChange: (newValues: string[]) => void;
  onMenuScrollToBottom?: () => void;
  options: string[] | any;
  reorderedOptions?: string[]; // Allow reordered options to be passed in
  placeholder?: string; // Placeholder text for the input
  disabled?: boolean;
}

const MultiSelectWithSearchField: React.FC<MultiSelectWithSearchFieldProps> = ({
  name,
  label,
  selectedValues,
  onChange,
  onMenuScrollToBottom,
  options,
  reorderedOptions,
  placeholder,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState<string>(""); // State for input value
  const [open, setOpen] = useState<boolean>(false); // State for dropdown visibility
  const menuRef = useRef<HTMLDivElement | null>(null); // Reference for scrollable menu
  const parentRef = useRef<HTMLDivElement | null>(null); // Reference for parent div width
  const [inputWidth, setInputWidth] = useState<number>(0); // State to store input width
const getOptionLabel = (option: any) => {
  if (typeof option === "string") return option;
  if (option?.label) return option.label;
  if (option?.name) return option.name;
  if (option?.value) return option.value;
  return String(option); // fallback
};

const getOptionValue = (option: any) => {
  if (typeof option === "string") return option;
  if (option?.id) return option.id;
  if (option?.value) return option.value;
  return getOptionLabel(option);
};

 // Build combined unique list
const availableOptions = [
  ...selectedValues,
  ...options.filter(
    (option: any) =>
      !selectedValues.some(
        (sel:any) => getOptionValue(sel) === getOptionValue(option)
      )
  ),
];

// Filter by input text safely
const filteredOptions = availableOptions.filter((option) => {
  const label = getOptionLabel(option);
  return label.toLowerCase().includes(inputValue.toLowerCase());
});


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleOptionToggle = (value: any, e?: React.MouseEvent) => {
    // Prevent event from bubbling up and closing the dropdown
    if (e) {
      e.stopPropagation();
    }
    const valueToCompare = getOptionValue(value);
    const isSelected = selectedValues.some((v:any) => getOptionValue(v) === valueToCompare);
    const updatedValues = isSelected
      ? selectedValues.filter((v:any) => getOptionValue(v) !== valueToCompare)
      : [...selectedValues, value];
    // Don't clear input or close dropdown - allow multiple selections
    setInputValue("");
    onChange(updatedValues);
    // Keep dropdown open for multiple selections
  };

  useEffect(() => {
    // Set the input width once the component mounts or updates
    if (parentRef.current) {
      setInputWidth(parentRef.current.offsetWidth);
    }

    const handleScroll = () => {
      const menu = menuRef.current;
      if (menu) {
        const isAtBottom =
          menu.scrollTop + menu.clientHeight >= menu.scrollHeight - 5;
        if (isAtBottom && onMenuScrollToBottom) {
          onMenuScrollToBottom(); // Trigger callback when scrolling reaches the bottom
        }
      }
    };

    const menu = menuRef.current;
    if (menu) menu.addEventListener("scroll", handleScroll);

    return () => {
      if (menu) menu.removeEventListener("scroll", handleScroll);
    };
  }, [onMenuScrollToBottom, parentRef.current?.offsetWidth]);

  // Clear input value whenever dropdown opens to ensure all options are visible
  useEffect(() => {
    if (open) {
      setInputValue("");
    }
  }, [open]);

  // Ensure only one MultiSelect dropdown is open at a time across the page
  useEffect(() => {
    const handleOtherDropdownOpen = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      // If some other MultiSelect (different name) is opened, close this one
      if (customEvent.detail && customEvent.detail !== name) {
        setOpen(false);
        setInputValue("");
      }
    };

    window.addEventListener("multi-select-open", handleOtherDropdownOpen);
    return () => {
      window.removeEventListener("multi-select-open", handleOtherDropdownOpen);
    };
  }, [name]);

  const openDropdown = () => {
    if (disabled) return;
    // Notify all MultiSelect instances that this one is opening
    window.dispatchEvent(new CustomEvent("multi-select-open", { detail: name }));
    setInputValue("");
    if (!open) {
      setOpen(true);
    }
  };

  return (
    <div className="w-full" ref={parentRef}>
      {/* Label for the Select Field */}
      {label && (
        <InputLabel
          htmlFor={name}
          sx={{
            color: '#e31c23', // Sets the text color to red
            // fontSize: '0.875rem', // Equivalent to Tailwind's `text-sm`
            // marginBottom: '0.25rem', // Equivalent to Tailwind's `mb-1`
            fontWeight:"bold",
         
          }}
        >
          {label}
        </InputLabel>
      )}

      <ClickAwayListener 
        onClickAway={() => {
          setOpen(false);
          setInputValue(""); // Clear input when closing dropdown
        }}
      >
        <div
          className="relative flex items-center justify-center border-gray-400 border rounded-lg"
          onClick={(e) => {
            if (disabled) return;
            // Open this dropdown and close others
            openDropdown();
            // Prevent event from bubbling to avoid conflicts
            e.stopPropagation();
          }}
        >
          {/* Input field */}
          {/* <div className="whitespace-nowrap ml-2 text-gray-700">
            {selectedValues?.length > 0 &&
              (selectedValues?.length === 1
                ? selectedValues[0]
                : "Multi Selected")}
          </div> */}
          <TextField
            id={name}
            name={name}
            value={inputValue}
            disabled={disabled} 
            placeholder={selectedValues?.length > 0 ?
                (selectedValues?.length === 1
                  ? selectedValues[0]
                  : "Multi Selected") : (placeholder || "")}
            onChange={handleInputChange}
            onFocus={(e) => {
              if (disabled) return;
              // Open this dropdown and close others
              openDropdown();
              // Select all text if there's any, so user can type immediately
              if (e.target instanceof HTMLInputElement) {
                e.target.select();
              }
            }}
            onClick={(e) => {
              if (disabled) return;
              // Ensure this dropdown is the only open one
              openDropdown();
              e.stopPropagation();
            }}
            fullWidth
            size="small"
            className="w-full text-[13px] rounded-md"
            sx={{
              '& .MuiOutlinedInput-root': {
                border: 'none', // Remove the border
                '& fieldset': {
                  border: 'none', // Remove the outlined border
                },
              },
            }}
          />

            {/* Popper for dropdown */}
            {open && !disabled && (
              <Popper
              open={open}
              anchorEl={document.querySelector(`input[name="${name}"]`)}
              placement="bottom-start"
              disablePortal
              style={{
                zIndex: 9999,
                width:"100%"
              }}
            >
                <div
                  ref={menuRef} // Attach the ref to the scrollable container
                  className="bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
                  onClick={(e) => {
                    // Prevent clicks inside the dropdown from closing it
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => {
                    // Prevent the dropdown from closing when clicking inside
                    e.stopPropagation();
                  }}
                >
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      <MenuItem
                        key={getOptionValue(option)}
                        value={getOptionValue(option)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOptionToggle(option, e);
                        }}
                        onMouseDown={(e) => {
                          // Prevent the dropdown from closing when clicking on menu items
                          // But allow the click to proceed
                          e.stopPropagation();
                        }}
                        // className={`flex justify-between text-sm ${
                        //   selectedValues.includes(option)
                        //     ? "bg-gray-100"
                        //     : "hover:bg-gray-200"
                        // }`}
                        // sx={{
                        //   padding:"1px",
                        // }}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '14px',
                          padding:"1.5px",
                          fontFamily:"Poppins",
                          backgroundColor: selectedValues.some((v:any) => getOptionValue(v) === getOptionValue(option)) ? '#E6E6E6' : 'inherit',
                          '&:hover': {
                            backgroundColor: selectedValues.some((v:any) => getOptionValue(v) === getOptionValue(option)) ? '#E6E6E6' : '#E0E0E0',
                          },
                        }}
                      >
                        <Checkbox
                         checked={selectedValues
  .some((v:any) => getOptionValue(v) === getOptionValue(option))}
                          size="small"
                          className="p-0"
                          sx={{
                            fontSize:"13px",
                            padding:"0",
                            fontFamily:"Poppins",
                            // fontWeight:"4px"
                            margin:0
                          }}
                        />
                        <ListItemText
                        primary={getOptionLabel(option)}
                          primaryTypographyProps={{
                            sx: {
                              // fontWeight:"4px",
                              fontFamily:"Poppins",
                              fontSize: '14px', // Ensure font size is 13px for the option text
                            },
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <ListItemText
                        primary="No Records Found"
                        primaryTypographyProps={{
                          sx: {
                            fontSize: '13px', // Apply font size to "No Records Found"
                            color: 'gray', // Optional: apply gray color to the disabled text
                          },
                        }}
                      />
                    </MenuItem>
                  )}
                </div>
              </Popper>
            )}
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default MultiSelectWithSearchField;